/**
 * WebSocket Service for Chat in user-modern — same as user-classic (communication-service Socket.IO).
 */
import { io, Socket } from 'socket.io-client'
import * as authService from './authService'
import { resolveUserChatTenantId } from '../lib/runtimeTenant'

interface ChatWebSocketEvents {
  message: (message: unknown) => void
  typing: (data: { conversationId: string; isTyping: boolean }) => void
  read: (data: { conversationId: string }) => void
  error: (error: unknown) => void
  connected: () => void
  disconnected: () => void
}

class ChatWebSocketService {
  private socket: Socket | null = null
  private listeners = new Map<string, Set<( ...args: unknown[]) => void>>()
  private isConnecting = false
  private isAuthenticated = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = Infinity
  private joinedConversationIds = new Set<string>()
  private pendingEmits: Array<{ event: string; data: unknown }> = []
  private browserListenersAttached = false
  private readonly typingCooldownMs = 1200
  private readonly lastTypingStateByConversation = new Map<string, boolean>()
  private readonly lastTypingSentAtByConversation = new Map<string, number>()
  private readonly recentInboundMessageIds = new Map<string, number>()

  private readonly handleStorageChange = (e: StorageEvent) => {
    if ((e.key === 'accessToken' || e.key === 'token' || e.key === 'user') && !e.newValue && this.socket?.connected) {
      this.disconnect()
    }
  }

  private readonly handleUserKicked = () => {
    this.disconnect()
  }

  private canEmitToChatRoom(): boolean {
    return Boolean(this.socket?.connected && this.isAuthenticated)
  }

  private extractInboundMessageId(data: unknown): string {
    const envelope = data as { message?: Record<string, unknown> }
    const raw = envelope?.message ?? data
    const record = raw as Record<string, unknown> | null
    if (!record || typeof record !== 'object') return ''
    return String(record.messageId ?? record.id ?? record._id ?? '')
  }

  private shouldSkipDuplicateInboundMessage(data: unknown): boolean {
    const messageId = this.extractInboundMessageId(data)
    if (!messageId) return false

    const now = Date.now()
    const seenAt = this.recentInboundMessageIds.get(messageId)
    if (seenAt != null && now - seenAt < 5000) return true

    this.recentInboundMessageIds.set(messageId, now)
    if (this.recentInboundMessageIds.size > 200) {
      for (const [key, at] of this.recentInboundMessageIds) {
        if (now - at > 5000) this.recentInboundMessageIds.delete(key)
      }
    }
    return false
  }

  private enqueueOrEmit(event: string, data: unknown): void {
    if (this.canEmitToChatRoom()) {
      this.socket?.emit(event, data)
      return
    }

    this.pendingEmits.push({ event, data })
  }

  private flushPendingConversationState(): void {
    if (!this.canEmitToChatRoom()) return

    this.joinedConversationIds.forEach((conversationId) => {
      this.socket?.emit('join_conversation', { conversationId })
    })

    while (this.pendingEmits.length > 0) {
      const next = this.pendingEmits.shift()
      if (!next) break
      this.socket?.emit(next.event, next.data)
    }
  }

  private attachBrowserListeners(): void {
    if (this.browserListenersAttached || typeof window === 'undefined') return
    window.addEventListener('storage', this.handleStorageChange)
    window.addEventListener('user_kicked', this.handleUserKicked)
    this.browserListenersAttached = true
  }

  private detachBrowserListeners(): void {
    if (!this.browserListenersAttached || typeof window === 'undefined') return
    window.removeEventListener('storage', this.handleStorageChange)
    window.removeEventListener('user_kicked', this.handleUserKicked)
    this.browserListenersAttached = false
  }

  connect(): void {
    if (this.socket?.connected || this.isConnecting) return

    const token = authService.getToken()
    if (!token) {
      console.warn('⚠️ [ChatWebSocket] No token available, cannot connect')
      return
    }

    const user = authService.getCurrentUser()
    const tenantId = resolveUserChatTenantId(user?.tenantId)
    this.isConnecting = true

    try {
      this.attachBrowserListeners()
      const wsUrl = window.location.origin
      if (import.meta.env.DEV) {
        console.log('🔌 [ChatWebSocket] Connecting path: /communication-service-api/socket.io tenantId:', tenantId)
      }
      // Polling only — WebSocket upgrade fails on production proxy chain.
      this.socket = io(wsUrl, {
        transports: ['polling'],
        upgrade: false,
        path: '/communication-service-api/socket.io',
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        withCredentials: true,
      })

      this.socket.on('connect', () => {
        if (import.meta.env.DEV) console.log('✅ [ChatWebSocket] Connected, authenticating...')
        this.isConnecting = false
        this.isAuthenticated = false
        this.reconnectAttempts = 0
        this.authenticate()
      })

      this.socket.on('connect_error', (error) => {
        console.debug('❌ [ChatWebSocket] Connection error:', error)
        this.isConnecting = false
        this.isAuthenticated = false
        this.reconnectAttempts++
      })

      this.socket.on('authenticated', () => {
        if (import.meta.env.DEV) console.log('✅ [ChatWebSocket] Authenticated')
        this.isAuthenticated = true
        this.flushPendingConversationState()
        this.emit('connected')
      })

      this.socket.on('disconnect', () => {
        if (import.meta.env.DEV) console.log('🔌 [ChatWebSocket] Disconnected')
        this.isAuthenticated = false
        this.emit('disconnected')
      })

      this.socket.on('message', (data: unknown) => {
        if (this.shouldSkipDuplicateInboundMessage(data)) return
        this.emit('message', data)
      })
      this.socket.on('message_sent', (data: { message?: unknown }) => {
        if (data.message) {
          if (this.shouldSkipDuplicateInboundMessage(data)) return
          this.emit('message', data.message)
        }
      })
      this.socket.on('new_message', (data: { message?: unknown }) => {
        if (this.shouldSkipDuplicateInboundMessage(data)) return
        this.emit('message', (data as { message?: unknown }).message ?? data)
      })
      this.socket.on('user_typing', (data: { conversationId: string; isTyping?: boolean }) => {
        this.emit('typing', { conversationId: data.conversationId, isTyping: data.isTyping !== false })
      })
      this.socket.on('typing', (data: { conversationId: string; isTyping: boolean }) => this.emit('typing', data))
      this.socket.on('messages_read', (data: { conversationId: string }) => this.emit('read', { conversationId: data.conversationId }))
      this.socket.on('read', (data: { conversationId: string }) => this.emit('read', data))
      this.socket.on('error', (error: unknown) => this.emit('error', error))
      this.socket.on('auth_error', (error: unknown) => this.emit('error', error))
      this.socket.on('message_error', (error: unknown) => this.emit('error', error))
      this.socket.on('conversation_error', (error: unknown) => this.emit('error', error))

    } catch (error) {
      console.error('❌ [ChatWebSocket] Failed to connect:', error)
      this.isConnecting = false
    }
  }

  private authenticate(): void {
    if (!this.socket?.connected) return
    const user = authService.getCurrentUser()
    const token = authService.getToken()
    const tenantId = resolveUserChatTenantId(user?.tenantId)
    const userId = (user as { _id?: string })?._id ?? user?.id ?? (user as { userId?: string })?.userId
    if (!userId || !token) {
      console.error('❌ [ChatWebSocket] Cannot authenticate: missing userId or token')
      return
    }
    this.socket.emit('authenticate', {
      userId,
      userType: 'user',
      token,
      metadata: { tenantId, username: user?.username ?? '' },
    })
  }

  joinConversation(conversationId: string): void {
    if (this.joinedConversationIds.has(conversationId) && this.canEmitToChatRoom()) {
      return
    }
    this.joinedConversationIds.add(conversationId)
    this.enqueueOrEmit('join_conversation', { conversationId })
  }

  leaveConversation(conversationId: string): void {
    this.joinedConversationIds.delete(conversationId)
    if (!this.canEmitToChatRoom()) return
    this.socket?.emit('leave_conversation', { conversationId })
  }

  sendMessage(
    conversationId: string,
    content: string,
    options?: {
      replyTo?: { id?: string; content?: string; senderName?: string }
    },
  ): void {
    this.enqueueOrEmit('send_message', {
      conversationId,
      content: { text: content },
      senderType: 'user',
      ...(options?.replyTo ? { replyTo: options.replyTo } : {}),
    })
  }

  sendTyping(conversationId: string, isTyping: boolean): void {
    const now = Date.now()
    const lastState = this.lastTypingStateByConversation.get(conversationId)
    const lastSentAt = this.lastTypingSentAtByConversation.get(conversationId) ?? 0

    if (lastState === isTyping && now - lastSentAt < this.typingCooldownMs) {
      return
    }

    this.lastTypingStateByConversation.set(conversationId, isTyping)
    this.lastTypingSentAtByConversation.set(conversationId, now)
    this.enqueueOrEmit(isTyping ? 'typing_start' : 'typing_stop', { conversationId })
  }

  on<K extends keyof ChatWebSocketEvents>(event: K, callback: ChatWebSocketEvents[K]): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set())
    this.listeners.get(event)!.add(callback as (...args: unknown[]) => void)
  }

  off<K extends keyof ChatWebSocketEvents>(event: K, callback: ChatWebSocketEvents[K]): void {
    this.listeners.get(event)?.delete(callback as (...args: unknown[]) => void)
  }

  private emit<K extends keyof ChatWebSocketEvents>(event: K, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((cb) => {
      try {
        cb(...args)
      } catch (err) {
        console.error(`❌ [ChatWebSocket] Error in ${event} callback:`, err)
      }
    })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
    this.detachBrowserListeners()
    this.listeners.clear()
    this.isConnecting = false
    this.isAuthenticated = false
    this.reconnectAttempts = 0
    this.joinedConversationIds.clear()
    this.lastTypingStateByConversation.clear()
    this.lastTypingSentAtByConversation.clear()
    this.recentInboundMessageIds.clear()
    this.pendingEmits = []
    this.emit('disconnected')
  }

  isConnected(): boolean {
    return this.canEmitToChatRoom()
  }
}

export const chatWebSocketService = new ChatWebSocketService()
